import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebase/firebaseConfig';
import AgregarEmpresaDb from '../firebase/AgregarEmpresaDb';
import {
  getDocs,
  collection,
  updateDoc,
  doc,
} from 'firebase/firestore';
import Alerta from './Alertas';
import validarRut from '../funciones/validarRut';
import { Link } from 'react-router-dom';
import { subirLogoEmpresa } from '../utils/logoEmpresa';
import {
  Table,
  Segment,
  Container,
  Input,
  Form,
  Header,
  Button,
  Icon,
  Image,
  Grid,
  Card,
  Label,
  Divider,
} from 'semantic-ui-react';

const EMPID_ST_INTERNO = String(
  process.env.REACT_APP_EMPID_STINTERNO || ''
).trim();

const MAX_USERS_BASICO = 3;

const AgregarEmpresa = () => {
  const user = auth.currentUser;

  const [empresa, setEmpresa] = useState('');
  const [rut, setRut] = useState('');
  const [leer, setLeer] = useState([]);
  const [alerta, cambiarAlerta] = useState({});
  const [estadoAlerta, cambiarEstadoAlerta] = useState(false);
  const [buscador, setBuscador] = useState('');
  const [buscadorDebounced, setBuscadorDebounced] = useState('');
  const [flag, setFlag] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState('');
  const [contratoMantencion, setContratoMantencion] = useState(false);
  const [usaLogisticmedSt, setUsaLogisticmedSt] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBuscadorDebounced(buscador), 250);
    return () => clearTimeout(t);
  }, [buscador]);

  const getData = async () => {
    try {
      if (!EMPID_ST_INTERNO) {
        setLeer([]);

        cambiarEstadoAlerta(true);
        cambiarAlerta({
          tipo: 'error',
          mensaje:
            'Falta configurar REACT_APP_EMPID_STINTERNO. No es posible cargar las empresas clientes.',
        });

        return;
      }

      const data = await getDocs(collection(db, 'empresas'));

      const empresasClientes = data.docs
        .filter((docItem) => docItem.id !== EMPID_ST_INTERNO)
        .map((docItem, index) => ({
          ...docItem.data(),
          id: docItem.id,
          id2: index + 1,
        }));

      setLeer(empresasClientes);
    } catch (error) {
      console.error('Error al cargar empresas:', error);

      setLeer([]);

      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: 'error',
        mensaje: 'No fue posible cargar las empresas clientes.',
      });
    }
  };

  useEffect(() => {
    getData();
  }, [flag]);

  const empresasOrdenadas = useMemo(() => {
    const copy = [...leer];
    copy.sort((a, b) => (a.empresa || '').localeCompare(b.empresa || ''));
    return copy;
  }, [leer]);

  const empresasFiltradas = useMemo(() => {
    const q = (buscadorDebounced || '').trim().toLowerCase();

    if (!q) return empresasOrdenadas;

    return empresasOrdenadas.filter((emp) => {
      const rutV = (emp.rut || '').toString().toLowerCase();
      const empV = (emp.empresa || '').toString().toLowerCase();
      const addV = (emp.useradd || emp.userAdd || '').toString().toLowerCase();
      const modV = (emp.usermod || emp.userMod || '').toString().toLowerCase();
      const planV = (emp.plan || '').toString().toLowerCase();
      const estadoV = (emp.estado || '').toString().toLowerCase();

      return (
        rutV.includes(q) ||
        empV.includes(q) ||
        addV.includes(q) ||
        modV.includes(q) ||
        planV.includes(q) ||
        estadoV.includes(q)
      );
    });
  }, [empresasOrdenadas, buscadorDebounced]);

  const totalEmpresas = leer.length;
  const empresasActivas = leer.filter((e) => (e.estado || 'ACTIVA') === 'ACTIVA').length;
  const empresasSuspendidas = leer.filter((e) => e.estado === 'SUSPENDIDA').length;

  const limpiarFormulario = () => {
    setRut('');
    setEmpresa('');
    setLogoFile(null);
    setPreviewLogo('');
    setContratoMantencion(false);
    setUsaLogisticmedSt(false);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    setLogoFile(file || null);

    if (file) {
      setPreviewLogo(URL.createObjectURL(file));
    } else {
      setPreviewLogo('');
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'SUSPENDIDA':
        return 'orange';
      case 'INACTIVA':
        return 'grey';
      case 'ACTIVA':
      default:
        return 'green';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    cambiarEstadoAlerta(false);
    cambiarAlerta({});

    const fechaAdd = new Date();
    const fechaMod = new Date();

    const rutIn = (rut || '').trim();
    const empIn = (empresa || '').trim().toUpperCase();

    const expresionRegularRut = /^[0-9]+[-|‐]{1}[0-9kK]{1}$/;
    const temp = rutIn.split('-');
    let digito = temp[1];

    if (digito === 'k' || digito === 'K') digito = -1;

    const validaR = validarRut(rutIn);

    const existeEmpresaNombre =
      leer.filter((emp) => (emp.empresa || '').trim() === empIn).length === 0;

    const existeEmpresaRut =
      leer.filter((emp) => (emp.rut || '').trim() === rutIn).length === 0;

    if (!rutIn) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({ tipo: 'error', mensaje: 'Campo Rut no puede estar vacío' });
      return;
    }

    if (!expresionRegularRut.test(rutIn)) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: 'error',
        mensaje: 'Formato incorrecto de rut. Sin puntos, solo guión',
      });
      return;
    }

    if (validaR !== parseInt(digito)) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({ tipo: 'error', mensaje: 'Rut no válido' });
      return;
    }

    if (!existeEmpresaRut) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: 'error',
        mensaje: 'Ya existe una Empresa registrada con ese Rut',
      });
      return;
    }

    if (!empIn) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({ tipo: 'error', mensaje: 'No ha ingresado una Empresa.' });
      return;
    }

    if (!existeEmpresaNombre) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({ tipo: 'error', mensaje: 'Ya existe esta Empresa' });
      return;
    }

    setIsSubmitting(true);

    try {
      const empresaId = await AgregarEmpresaDb({
        rut: rutIn,
        empresa: empIn,
        userAdd: user?.email || '',
        userMod: user?.email || '',
        fechaAdd,
        fechaMod,
        logoUrl: '',
        logoPath: '',
        versionLogo: 1,
        estado: 'ACTIVA',
        plan: 'BASICO',
        maxUsers: MAX_USERS_BASICO,
      });

      if (logoFile) {
        const { logoUrl, logoPath } = await subirLogoEmpresa({
          file: logoFile,
          empId: empresaId,
        });

        await updateDoc(doc(db, 'empresas', empresaId), {
          logoUrl,
          logoPath,
          versionLogo: 1,
          fechamod: new Date(),
          usermod: user?.email || '',
        });
      }

      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: 'exito',
        mensaje: 'Empresa registrada correctamente',
      });

      limpiarFormulario();
      setFlag((v) => !v);
    } catch (err) {
      console.error('Error al registrar empresa:', err);

      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: 'error',
        mensaje: err.message || 'Error al registrar empresa',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid style={{ padding: '22px 28px' }}>
      <Segment
        basic
        style={{
          padding: 0,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #0f6f78 0%, #16b9cf 100%)',
            borderRadius: 16,
            padding: '26px 30px',
            color: '#fff',
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
          }}
        >
          <Grid stackable verticalAlign="middle">
            <Grid.Column width={10}>
              <Header as="h2" inverted style={{ margin: 0 }}>
                <Icon name="building outline" />
                <Header.Content>
                  Empresas Clientes
                  <Header.Subheader style={{ color: 'rgba(255,255,255,0.85)' }}>
                    Administra empresas, branding, planes y acceso comercial
                  </Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>

            <Grid.Column width={6} textAlign="right">
              <Label size="large" basic style={{ color: '#fff', borderColor: '#fff' }}>
                <Icon name="user shield" />
                Backoffice
              </Label>
            </Grid.Column>
          </Grid>
        </div>
      </Segment>

      <Grid stackable>
        <Grid.Column width={5}>
          <Segment
            raised
            style={{
              borderRadius: 16,
              border: '1px solid #e7edf2',
              boxShadow: '0 8px 22px rgba(0,0,0,0.07)',
            }}
          >
            <Header as="h3" color="teal">
              <Icon name="plus circle" />
              <Header.Content>
                Registrar empresa
                <Header.Subheader> Nuevo tenant HomeCare</Header.Subheader>
              </Header.Content>
            </Header>

            <Divider />

            <Form onSubmit={handleSubmit}>
              <Form.Input
                fluid
                label="RUT"
                maxLength={10}
                placeholder="Ej: 17579501-4"
                name="rut"
                value={rut}
                onChange={(e) => setRut(e.target.value)}
                icon={{ name: 'id card outline', color: 'teal' }}
                iconPosition="left"
                disabled={isSubmitting}
              />

              <Form.Input
                fluid
                label="Nombre empresa"
                placeholder="Nombre comercial o razón social"
                name="empresa"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                icon={{ name: 'building outline', color: 'teal' }}
                iconPosition="left"
                disabled={isSubmitting}
              />

              <Form.Field>
                <label>Logo empresa</label>

                <div
                  style={{
                    border: '1px dashed #b9c7d3',
                    borderRadius: 14,
                    padding: 16,
                    background: '#f8fbfd',
                    textAlign: 'center',
                  }}
                >
                  {previewLogo ? (
                    <Image
                      src={previewLogo}
                      centered
                      style={{
                        maxHeight: 95,
                        maxWidth: 180,
                        objectFit: 'contain',
                        background: '#fff',
                        padding: 8,
                        borderRadius: 10,
                        border: '1px solid #e6e6e6',
                        marginBottom: 12,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        height: 95,
                        display: 'grid',
                        placeItems: 'center',
                        color: '#7b8a97',
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <Icon name="image outline" size="big" />
                        <div style={{ marginTop: 6, fontSize: 13 }}>
                          PNG, JPG o WEBP
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    as="label"
                    icon
                    basic
                    color="teal"
                    fluid
                    disabled={isSubmitting}
                  >
                    <Icon name="upload" />
                    Seleccionar logo
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleLogoChange}
                    />
                  </Button>

                  {logoFile && (
                    <div style={{ fontSize: 12, marginTop: 8, color: '#607080' }}>
                      {logoFile.name}
                    </div>
                  )}
                </div>
              </Form.Field>

              <Segment
                basic
                style={{
                  background: '#f7fafc',
                  borderRadius: 12,
                  marginTop: 14,
                  padding: 14,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>Plan inicial</span>
                  <b>BASICO</b>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    marginTop: 6,
                  }}
                >
                  <span>Usuarios incluidos</span>
                  <b>{MAX_USERS_BASICO}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 6 }}>
                  <span>Estado</span>
                  <Label color="green" size="mini">ACTIVA</Label>
                </div>
              </Segment>

              <Segment
                basic
                style={{
                  background: '#f0fdfa',
                  borderRadius: 12,
                  marginTop: 14,
                  padding: 14,
                  border: '1px solid #ccfbf1',
                }}
              >
                <Header as="h4" color="teal" style={{ marginBottom: 12 }}>
                  <Icon name="wrench" />
                  Contrato de mantención
                </Header>

                <Form.Checkbox
                  toggle
                  label="Cliente con contrato de mantención"
                  checked={contratoMantencion}
                  onChange={(e, { checked }) => {
                    setContratoMantencion(checked);

                    if (!checked) {
                      setUsaLogisticmedSt(false);
                    }
                  }}
                  disabled={isSubmitting}
                />

                <Form.Checkbox
                  toggle
                  label="Derivar equipos cubiertos a LogisticMedST"
                  checked={usaLogisticmedSt}
                  onChange={(e, { checked }) => setUsaLogisticmedSt(checked)}
                  disabled={isSubmitting || !contratoMantencion}
                />

                <div style={{ marginTop: 8, color: '#64748b', fontSize: 12 }}>
                  La cobertura por familias, tipos o equipos se configura después en Backoffice.
                </div>
              </Segment>

              <Button
                primary
                fluid
                size="large"
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                style={{
                  marginTop: 16,
                  borderRadius: 10,
                  background: '#16B9CF',
                }}
              >
                <Icon name="save outline" />
                Registrar Empresa
              </Button>
            </Form>
          </Segment>
        </Grid.Column>

        <Grid.Column width={11}>
          <Grid columns={3} stackable>
            <Grid.Column>
              <Card fluid style={{ borderRadius: 14 }}>
                <Card.Content>
                  <Icon name="building" color="teal" size="large" />
                  <Card.Header style={{ marginTop: 10 }}>{totalEmpresas}</Card.Header>
                  <Card.Meta>Total empresas</Card.Meta>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card fluid style={{ borderRadius: 14 }}>
                <Card.Content>
                  <Icon name="check circle" color="green" size="large" />
                  <Card.Header style={{ marginTop: 10 }}>{empresasActivas}</Card.Header>
                  <Card.Meta>Empresas activas</Card.Meta>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card fluid style={{ borderRadius: 14 }}>
                <Card.Content>
                  <Icon name="pause circle" color="orange" size="large" />
                  <Card.Header style={{ marginTop: 10 }}>{empresasSuspendidas}</Card.Header>
                  <Card.Meta>Suspendidas</Card.Meta>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>

          <Segment
            raised
            style={{
              borderRadius: 16,
              border: '1px solid #e7edf2',
              boxShadow: '0 8px 22px rgba(0,0,0,0.07)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
                marginBottom: 14,
              }}
            >
              <div>
                <Header as="h3" color="blue" style={{ margin: 0 }}>
                  Listado de Empresas
                </Header>
                <div style={{ color: '#6b7280', fontSize: 13 }}>
                  {empresasFiltradas.length} resultado(s)
                </div>
              </div>

              <Input
                style={{ minWidth: 320 }}
                placeholder="Buscar por rut, empresa, plan o estado"
                icon={{ name: 'search', color: 'blue' }}
                value={buscador}
                onChange={(e) => setBuscador(e.target.value)}
                action={
                  buscador
                    ? {
                      icon: 'close',
                      onClick: () => setBuscador(''),
                      title: 'Limpiar',
                    }
                    : undefined
                }
              />
            </div>

            <div style={{ maxHeight: 560, overflowY: 'auto', overflowX: 'auto' }}>
              <Table
                basic="very"
                selectable
                compact
                style={{
                  fontSize: 13,
                  minWidth: 900,
                }}
              >
                <Table.Header
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#fff',
                  }}
                >
                  <Table.Row>
                    <Table.HeaderCell>N°</Table.HeaderCell>
                    <Table.HeaderCell>Empresa</Table.HeaderCell>
                    <Table.HeaderCell>RUT</Table.HeaderCell>
                    <Table.HeaderCell>Plan</Table.HeaderCell>
                    <Table.HeaderCell>Usuarios</Table.HeaderCell>
                    <Table.HeaderCell>Estado</Table.HeaderCell>
                    <Table.HeaderCell>Modificado por</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Acción</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {empresasFiltradas.map((item, index) => (
                    <Table.Row key={item.id ?? index}>
                      <Table.Cell>{index + 1}</Table.Cell>

                      <Table.Cell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 10,
                              background: '#eef9fb',
                              display: 'grid',
                              placeItems: 'center',
                              color: '#168b9b',
                            }}
                          >
                            <Icon name="building outline" />
                          </div>

                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {item.empresa}
                            </div>
                            <div style={{ fontSize: 11, color: '#8a94a0' }}>
                              ID: {item.id}
                            </div>
                          </div>
                        </div>
                      </Table.Cell>

                      <Table.Cell>{item.rut}</Table.Cell>

                      <Table.Cell>
                        <Label color="blue" basic>
                          {item.plan || 'BASICO'}
                        </Label>
                      </Table.Cell>

                      <Table.Cell>
                        <Icon name="users" color="grey" />
                        {item.maxUsers || MAX_USERS_BASICO}
                      </Table.Cell>

                      <Table.Cell>
                        <Label color={getEstadoColor(item.estado || 'ACTIVA')} size="small">
                          {item.estado || 'ACTIVA'}
                        </Label>
                      </Table.Cell>

                      <Table.Cell>{item.usermod || item.userMod || '-'}</Table.Cell>

                      <Table.Cell textAlign="center">
                        <Button
                          as={Link}
                          to={`/backofice/actualizaempresa/${item.id}`}
                          icon
                          basic
                          color="blue"
                          size="small"
                          title="Editar Empresa"
                        >
                          <Icon name="edit outline" />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}

                  {empresasFiltradas.length === 0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan="8"
                        textAlign="center"
                        style={{ padding: 40, color: '#7b8794' }}
                      >
                        <Icon name="search" size="large" />
                        <div style={{ marginTop: 8 }}>
                          No se encontraron empresas.
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      <Alerta
        tipo={alerta.tipo}
        mensaje={alerta.mensaje}
        estadoAlerta={estadoAlerta}
        cambiarEstadoAlerta={cambiarEstadoAlerta}
      />
    </Container>
  );
};

export default AgregarEmpresa;